import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ComponentProps } from 'react'

import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import useSwitchRegion from 'hooks/useSwitchRegion'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { MINIMUM_POINTS_TO_APPLY } from 'loyalty/constants/loyaltyBurn'
import LoyaltyBurnInfoModal from 'loyalty/components/LoyaltyBurnInfoModal'
import type LoyaltyBurnSliderBase from 'loyalty/components/LoyaltyBurnSliderBase'
import { SET_SEARCH_BURN_ENABLED } from 'actions/actionConstants'

export interface UseOfferBurnSliderStateOptions {
  /** Product-specific eligibility gate, e.g. getIsLoyaltyEnabled or getIsExperienceLoyaltyBurnEnabled. */
  isLoyaltyEnabled: boolean
  /**
   * Maximum points the user could apply, already resolved by the caller (e.g. from a
   * batch of search-result calculations, or a single offer/ticket price calculation).
   */
  maxBurnPoints: number
  /** Whether maxBurnPoints is still being calculated. */
  isLoadingMax: boolean
  /** Dispatches the chosen points value into the caller's own offer-page state. */
  onPointsToApplyChange: (pointsToApply: number | undefined) => void
  /** Extra gate to hide the slider entirely regardless of eligibility, e.g. charterBundleEnabled. */
  disabled?: boolean
  /**
   * When true, a user's manually chosen amount is preserved across re-renders that
   * change maxBurnPoints (only clamped down if it now exceeds the new max), rather
   * than always snapping to the current max. Off by default to match the original
   * hotel/cruise/tour slider behaviour.
   */
  stickyUserChoice?: boolean
}

type SliderBaseProps = ComponentProps<typeof LoyaltyBurnSliderBase>

export interface UseOfferBurnSliderStateResult {
  shouldShow: boolean
  sliderProps: Omit<SliderBaseProps, 'maxPointsTooltipDescription'>
}

function useOfferBurnSliderState(options: UseOfferBurnSliderStateOptions): UseOfferBurnSliderStateResult {
  const { isLoyaltyEnabled, maxBurnPoints, isLoadingMax, onPointsToApplyChange, disabled = false, stickyUserChoice = false } = options

  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const [tier] = useCurrentLoyaltyTier()

  const searchBurnEnabled = useAppSelector(state => state.loyalty.hasSelectedBurnToggle)
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const isInLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const loyaltyAccountRegion = useAppSelector(getLoyaltyAccountRegion)

  const loyaltyCurrencyLabel = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyAccountRegion)?.currencyCode
  }, [loyaltyAccountRegion])

  const [switchRegionPath, handleSwitchRegion] = useSwitchRegion(loyaltyAccountRegion)

  const minBurnPoints = MINIMUM_POINTS_TO_APPLY

  const [localValue, setLocalValue] = useState<number>(() => userPoints)
  // Only used when stickyUserChoice is enabled: tracks whether the user has explicitly
  // chosen an amount (via the slider or reset). Until they do, the applied amount always
  // tracks the (possibly changing) max; once they have, it's remembered across re-renders
  // and only clamped down if it now exceeds the new max.
  const [hasUserAdjusted, setHasUserAdjusted] = useState(false)

  useEffect(() => {
    if (!searchBurnEnabled) return
    if (!stickyUserChoice || !hasUserAdjusted) {
      setLocalValue(maxBurnPoints)
      onPointsToApplyChange(maxBurnPoints)
      return
    }
    if (localValue > maxBurnPoints) {
      setLocalValue(maxBurnPoints)
      onPointsToApplyChange(maxBurnPoints)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- localValue intentionally excluded when !stickyUserChoice to preserve original always-track-max behaviour
  }, [searchBurnEnabled, maxBurnPoints, stickyUserChoice, hasUserAdjusted, onPointsToApplyChange])

  const debouncedSliderDispatch = useDebouncedCallback(onPointsToApplyChange, 100, [onPointsToApplyChange])

  const onSliderChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>, value: number) => {
    if (stickyUserChoice) setHasUserAdjusted(true)
    setLocalValue(value)
    debouncedSliderDispatch(value)
  }, [stickyUserChoice, debouncedSliderDispatch])

  const handleToggle = useCallback(async() => {
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    if (searchBurnEnabled) {
      if (stickyUserChoice) setHasUserAdjusted(false)
      setLocalValue(maxBurnPoints)
      dispatch({ type: SET_SEARCH_BURN_ENABLED, enabled: false })
      onPointsToApplyChange(undefined)
    } else {
      if (stickyUserChoice) setHasUserAdjusted(false)
      setLocalValue(maxBurnPoints)
      dispatch({ type: SET_SEARCH_BURN_ENABLED, enabled: true })
      onPointsToApplyChange(maxBurnPoints)
    }
  }, [isLoggedIn, searchBurnEnabled, maxBurnPoints, stickyUserChoice, dispatch, onPointsToApplyChange, showModal])

  const handleReset = useCallback(() => {
    if (stickyUserChoice) setHasUserAdjusted(true)
    setLocalValue(minBurnPoints)
    onPointsToApplyChange(minBurnPoints)
  }, [stickyUserChoice, onPointsToApplyChange, minBurnPoints])

  const handleInfoClick = useCallback(() => {
    showModal(<LoyaltyBurnInfoModal />)
  }, [showModal])

  const shouldShow = useMemo(() => {
    if (disabled) return false
    if (!isLoyaltyEnabled) return false
    if (!isLoggedIn) return true
    if (!hasLoyaltyAccount) return false
    if (!isInLoyaltyRegion) return true
    return userPoints >= minBurnPoints
  }, [disabled, isLoyaltyEnabled, isLoggedIn, hasLoyaltyAccount, isInLoyaltyRegion, userPoints, minBurnPoints])

  const sliderProps = useMemo<Omit<SliderBaseProps, 'maxPointsTooltipDescription'>>(() => ({
    isLoggedIn,
    isEnabled: searchBurnEnabled,
    onToggle: handleToggle,
    tier,
    hasLoyaltyAccount,
    userPoints,
    maxBurnPoints,
    minBurnPoints,
    loading: isLoadingMax,
    localValue,
    onSliderChange,
    onReset: handleReset,
    onInfoClick: handleInfoClick,
    wrongRegion: isLoggedIn && !isInLoyaltyRegion,
    switchRegionPath,
    onSwitchRegion: handleSwitchRegion,
    loyaltyCurrencyLabel,
  }), [
    isLoggedIn,
    searchBurnEnabled,
    handleToggle,
    tier,
    hasLoyaltyAccount,
    userPoints,
    maxBurnPoints,
    minBurnPoints,
    isLoadingMax,
    localValue,
    onSliderChange,
    handleReset,
    handleInfoClick,
    isInLoyaltyRegion,
    switchRegionPath,
    handleSwitchRegion,
    loyaltyCurrencyLabel,
  ])

  return { shouldShow, sliderProps }
}

export default useOfferBurnSliderState
